<script lang="ts" setup>
import { $isCustomizationActive } from '#components/product-customization-board'
import { useStore } from '@nanostores/vue'
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'
import { clearBodyLocks, lock } from 'tua-body-scroll-lock'
import { onMounted, ref, watch } from 'vue'
import ProductCustomizationForm from './ProductCustomizationForm.vue'
import ProductCustomizationImageComparison from './ProductCustomizationImageComparison.vue'
import ProductCustomizationReviewConfirmation from './ProductCustomizationReviewConfirmation.vue'
import IonColorPaletteOutline from '#components/icons/IonColorPaletteOutline.vue'

const isCustomizationActive = useStore($isCustomizationActive)

const toggleCustomizationActive = () => {
  $isCustomizationActive.set(!isCustomizationActive.value)
}

const renderer = ref<HTMLElement | null>(null)
const breakpoints = useBreakpoints(breakpointsTailwind)
const isOnMobile = breakpoints.smaller('sm')

watch(isCustomizationActive, (isActive) => {
  if (!isOnMobile.value) return

  if (isActive) {
    renderer.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })

    setTimeout(() => {
      lock()
    }, 500)
  } else {
    clearBodyLocks()
  }
})

onMounted(() => {
  // Auto active customization on first load, setTimeout to make sure everything is loaded
  // so scrollIntoView and scroll lock in watcher above can work properly
  setTimeout(() => {
    $isCustomizationActive.set(true)
  }, 1000)
})
</script>

<template>
  <div class="product-customization-board">
    <div class="product-customization-board__renderer-wrapper">
      <div ref="renderer" class="product-customization-board__renderer">
        <ProductCustomizationImageComparison />
      </div>

      <button
        id="productCustomizationToggler"
        type="button"
        aria-controls="productCustomizationForm"
        :aria-expanded="isCustomizationActive ? 'true' : 'false'"
        class="product-customization-board__form-toggler button button--primary"
        :class="{
          'tw-pointer-events-none tw-translate-y-full': isCustomizationActive
        }"
        @click="toggleCustomizationActive"
      >
        <IonColorPaletteOutline class="tw-h-5 tw-w-5" />
        Customize
      </button>
    </div>

    <ProductCustomizationForm />
  </div>

  <ProductCustomizationReviewConfirmation />
</template>

<style lang="postcss" scoped>
.product-customization-board {
  @apply tw-relative tw-flex tw-w-full tw-flex-col tw-items-center;
}

.product-customization-board__renderer-wrapper {
  @apply tw-relative tw-flex tw-aspect-square tw-w-full tw-items-center tw-justify-center tw-overflow-hidden tw-border tw-border-b-0 tw-border-solid tw-shadow-lg;
}

.product-customization-board__renderer {
  @apply tw-relative tw-z-[2] tw-flex tw-h-full tw-w-full tw-flex-col tw-items-center tw-justify-center tw-border-b tw-bg-white tw-p-4 [border-bottom-style:solid] lg:tw-px-10 lg:tw-py-8;
}

.product-customization-board__form-toggler {
  @apply tw-absolute tw-bottom-0 tw-right-0 tw-z-[3] tw-min-h-[unset] tw-justify-between tw-gap-2 tw-px-3 tw-py-2 tw-text-sm tw-transition-all tw-duration-300 tw-ease-in-out lg:tw-px-4 lg:tw-py-3 lg:tw-text-base;
}
</style>
